<template>
  <div class="td-phone-input" :class="{ 'is-disabled': disabled, 'is-focused': isOpen }">
    <button
      type="button"
      class="td-phone-input__country"
      :disabled="disabled"
      @click="toggleDropdown"
    >
      <span
        class="td-phone-input__flag"
        :class="`flag-${selectedCountry.iso.toLowerCase()}`"
        :aria-label="selectedCountry.name"
      ></span>
      <i class="td-phone-input__chevron fa-solid fa-chevron-down"></i>
    </button>

    <input
      ref="phoneInput"
      type="tel"
      class="td-phone-input__field"
      :placeholder="placeholder"
      :value="displayNumber"
      :disabled="disabled"
      :required="required"
      autocomplete="tel"
      inputmode="tel"
      @input="handleInput"
      @focus="handleFocus"
      @blur="$emit('blur')"
    >

    <div v-if="isOpen" class="td-phone-input__menu">
      <input
        ref="searchInput"
        v-model.trim="search"
        type="search"
        class="td-phone-input__search"
        :placeholder="searchPlaceholder"
        @keydown.esc="closeDropdown"
      >
      <button
        v-for="country in filteredCountries"
        :key="country.iso"
        type="button"
        class="td-phone-input__option"
        @click="selectCountry(country)"
      >
        <span
          class="td-phone-input__flag td-phone-input__flag--option"
          :class="`flag-${country.iso.toLowerCase()}`"
          :aria-label="country.name"
        ></span>
        <span class="td-phone-input__option-name">{{ country.name }}</span>
        <span class="td-phone-input__dial">{{ country.dial }}</span>
      </button>
      <div v-if="!filteredCountries.length" class="td-phone-input__empty">
        {{ emptyText }}
      </div>
    </div>
  </div>
</template>

<script>
const COUNTRIES = [
  { iso: 'MX', name: 'Mexico', dial: '+52', flag: '🇲🇽' },
  { iso: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
  { iso: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { iso: 'AR', name: 'Argentina', dial: '+54', flag: '🇦🇷' },
  { iso: 'BR', name: 'Brazil', dial: '+55', flag: '🇧🇷' },
  { iso: 'CL', name: 'Chile', dial: '+56', flag: '🇨🇱' },
  { iso: 'CO', name: 'Colombia', dial: '+57', flag: '🇨🇴' },
  { iso: 'CR', name: 'Costa Rica', dial: '+506', flag: '🇨🇷' },
  { iso: 'DO', name: 'Dominican Republic', dial: '+1', flag: '🇩🇴' },
  { iso: 'EC', name: 'Ecuador', dial: '+593', flag: '🇪🇨' },
  { iso: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸' },
  { iso: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { iso: 'GT', name: 'Guatemala', dial: '+502', flag: '🇬🇹' },
  { iso: 'PA', name: 'Panama', dial: '+507', flag: '🇵🇦' },
  { iso: 'PE', name: 'Peru', dial: '+51', flag: '🇵🇪' },
  { iso: 'UY', name: 'Uruguay', dial: '+598', flag: '🇺🇾' }
];

export default {
  name: 'PhoneInputComponent',
  props: {
    modelValue: { type: [String, Number], default: '' },
    placeholder: { type: String, default: 'Phone number' },
    searchPlaceholder: { type: String, default: 'Search country' },
    emptyText: { type: String, default: 'No countries found' },
    defaultCountry: { type: String, default: 'MX' },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'blur', 'country-change'],
  data() {
    return {
      countries: COUNTRIES,
      selectedIso: this.defaultCountry,
      isOpen: false,
      search: ''
    };
  },
  computed: {
    selectedCountry() {
      return this.countries.find(country => country.iso === this.selectedIso) || this.countries[0];
    },
    filteredCountries() {
      const term = this.search.toLowerCase();
      if (!term) return this.countries;
      return this.countries.filter(country => {
        return country.name.toLowerCase().includes(term)
          || country.iso.toLowerCase().includes(term)
          || country.dial.includes(term);
      });
    },
    displayNumber() {
      const raw = String(this.modelValue || '').trim();
      const dial = this.selectedCountry.dial;
      if (raw.startsWith(dial)) {
        return raw.slice(dial.length).trim();
      }
      return raw;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        this.detectCountryFromValue(value);
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      }
    },
    closeDropdown() {
      this.isOpen = false;
      this.search = '';
    },
    handleFocus() {
      this.$emit('country-change', this.selectedCountry);
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    selectCountry(country) {
      this.selectedIso = country.iso;
      this.closeDropdown();
      this.emitValue(this.displayNumber);
      this.$emit('country-change', country);
      this.$nextTick(() => this.$refs.phoneInput?.focus());
    },
    handleInput(event) {
      this.emitValue(event.target.value);
    },
    emitValue(localNumber) {
      const value = String(localNumber || '').trim();
      if (!value) {
        this.$emit('update:modelValue', '');
        return;
      }
      if (value.startsWith('+')) {
        this.$emit('update:modelValue', value);
        return;
      }
      this.$emit('update:modelValue', `${this.selectedCountry.dial} ${value}`.trim());
    },
    detectCountryFromValue(value) {
      const raw = String(value || '').trim();
      if (!raw.startsWith('+')) return;
      const match = this.countries
        .slice()
        .sort((a, b) => b.dial.length - a.dial.length)
        .find(country => raw.startsWith(country.dial));
      if (match) {
        this.selectedIso = match.iso;
      }
    }
  }
};
</script>

<style scoped>
.td-phone-input {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 38px;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.td-phone-input.is-focused {
  border-color: #86b7fe;
  box-shadow: 0 0 0 .25rem rgba(13, 110, 253, .15);
}

.td-phone-input.is-disabled {
  background: #e9ecef;
  opacity: 1;
}

.td-phone-input__country {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 72px;
  padding: 0 12px;
  border: 0;
  border-right: 1px solid #ced4da;
  border-radius: 0.375rem 0 0 0.375rem;
  background: #fbfcff;
  cursor: pointer;
}

.td-phone-input__country:disabled {
  cursor: not-allowed;
}

.td-phone-input__flag {
  border: 1px solid rgba(0, 0, 0, .12);
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, .55) inset;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  height: 16px;
  justify-content: center;
  line-height: 1;
  overflow: hidden;
  position: relative;
  width: 24px;
}

.td-phone-input__flag--option {
  align-self: center;
}

.flag-mx { background: linear-gradient(90deg, #006847 0 33.333%, #fff 33.333% 66.666%, #ce1126 66.666% 100%); }
.flag-mx::before {
  background: #a67c00;
  border: 1px solid rgba(0, 104, 71, .55);
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, .65);
  content: "";
  height: 4px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 4px;
}
.flag-us { background: repeating-linear-gradient(180deg, #b22234 0 7.69%, #fff 7.69% 15.38%); }
.flag-us::before {
  background: #3c3b6e;
  content: "";
  height: 54%;
  left: 0;
  position: absolute;
  top: 0;
  width: 45%;
}
.flag-ca { background: linear-gradient(90deg, #d52b1e 0 25%, #fff 25% 75%, #d52b1e 75% 100%); }
.flag-ar { background: linear-gradient(180deg, #74acdf 0 33.333%, #fff 33.333% 66.666%, #74acdf 66.666% 100%); }
.flag-br { background: #009b3a; }
.flag-br::before {
  background: #ffdf00;
  clip-path: polygon(50% 8%, 92% 50%, 50% 92%, 8% 50%);
  content: "";
  inset: 2px 4px;
  position: absolute;
}
.flag-cl { background: linear-gradient(180deg, #fff 0 50%, #d52b1e 50% 100%); }
.flag-cl::before { background: #0039a6; content: ""; height: 50%; left: 0; position: absolute; top: 0; width: 40%; }
.flag-co { background: linear-gradient(180deg, #fcd116 0 50%, #003893 50% 75%, #ce1126 75% 100%); }
.flag-cr { background: linear-gradient(180deg, #002b7f 0 18%, #fff 18% 32%, #ce1126 32% 68%, #fff 68% 82%, #002b7f 82% 100%); }
.flag-do { background: linear-gradient(90deg, #002d62 0 44%, #fff 44% 56%, #ce1126 56% 100%); }
.flag-ec { background: linear-gradient(180deg, #ffdd00 0 50%, #034ea2 50% 75%, #ed1c24 75% 100%); }
.flag-es { background: linear-gradient(180deg, #aa151b 0 25%, #f1bf00 25% 75%, #aa151b 75% 100%); }
.flag-fr { background: linear-gradient(90deg, #0055a4 0 33.333%, #fff 33.333% 66.666%, #ef4135 66.666% 100%); }
.flag-gb { background: #012169; }
.flag-gb::before { background: #fff; content: ""; height: 18%; left: 0; position: absolute; top: 41%; width: 100%; }
.flag-gb::after { background: #c8102e; content: ""; height: 10%; left: 0; position: absolute; top: 45%; width: 100%; }
.flag-gt { background: linear-gradient(90deg, #4997d0 0 33.333%, #fff 33.333% 66.666%, #4997d0 66.666% 100%); }
.flag-pa { background: linear-gradient(90deg, #fff 0 50%, #d21034 50% 100%); }
.flag-pe { background: linear-gradient(90deg, #d91023 0 33.333%, #fff 33.333% 66.666%, #d91023 66.666% 100%); }
.flag-uy { background: repeating-linear-gradient(180deg, #fff 0 11.1%, #0038a8 11.1% 22.2%); }
.flag-uy::before { background: #fff9c7; content: ""; height: 45%; left: 0; position: absolute; top: 0; width: 38%; }

.td-phone-input__chevron {
  color: #475467;
  font-size: 10px;
  line-height: 1;
}

.td-phone-input__field {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  padding: 0.375rem 0.75rem;
  background: transparent;
  color: #212529;
}

.td-phone-input__field:disabled {
  color: #6c757d;
}

.td-phone-input__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: min(360px, 100%);
  max-height: 280px;
  overflow: auto;
  z-index: 1080;
  padding: 8px;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 18px 40px rgba(16, 24, 40, .16);
}

.td-phone-input__search {
  width: 100%;
  margin-bottom: 6px;
  padding: 8px 10px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  outline: 0;
}

.td-phone-input__option {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.td-phone-input__option:hover {
  background: #f4f1ff;
}

.td-phone-input__option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-phone-input__dial {
  color: #667085;
  font-weight: 700;
}

.td-phone-input__empty {
  padding: 10px;
  color: #667085;
  font-size: 13px;
}
</style>
